import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// import { logout } from "../../redux/features/auth/authSlice.tsx";
import { useAppDispatch } from "../../hooks/useAppDispatch.tsx";
import { toast } from "react-toastify";
import CommonConfirmationModal from "./CommonConfirmationModal.tsx";
import { logout } from "../../redux/features/auth/logoutSlice.tsx";
import { logout_resetState } from "../../redux/features/auth/authSlice.tsx";
import { useAppSelector } from "../../hooks/useAppSelector.tsx";

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

// Create a custom ListItem that works with React Router
const CustomListItem = styled(ListItem)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: "#a9edf35c",
    borderLeft: "4px solid #A9EDF3",
  },
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
}));

const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);


  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const profile = useAppSelector((state) => state.profile.data);

  // State to manage sidebar open/close
  const [localIsOpen, setLocalIsOpen] = useState(true);

  // Effect to handle sidebar state on screen size change
  useEffect(() => {
    if (isMobileScreen) {
      // On mobile, sidebar should start closed
      setLocalIsOpen(true);
    } else {
      // On larger screens, sync with prop
      setLocalIsOpen(isOpen);
    }
  }, [isMobileScreen, isOpen]);

  useEffect(() => {
    if (profile?.data) {
      setIsLoading(false);
    }
  }, [profile]);



  const isSubAdmin = profile?.data?.role === "SUB_ADMIN";
  const viewModules = profile?.data?.viewModule || [];

  const allLinks = [
    { name: "Dashboard", path: "/dashboard", icon: "dashboard-Icon.png", moduleKey: "dashboard" },
    {
      name: "User Management",
      path: "/user-management",
      icon: "userManagement-Icon.png",
      moduleKey: "user-management"
    },
    { 
      name: "Rooms", 
      path: "/room-management", 
      icon: "rooms-Icon.png",
      moduleKey: "room-management"
    },
    {
      name: "Testimonials",
      path: "/testimonial-management",
      icon: "testimonials-Icon.png",
      moduleKey: "testimonial-management"
    },
    {
      name: "Booking Request",
      path: "/booking-management",
      icon: "bookings-Icon.png",
      moduleKey: "booking-management"
    },
    {
      name: "Reservations",
      path: "/reservations",
      icon: "bookings-Icon.png",
      moduleKey: "booking-management" // Using same moduleKey as Booking Request
    },
    {
      name: "Static Content",
      path: "/static-content/privacy-policy",
      icon: "content-Icon.png",
      moduleKey: "content-management"
    },
    {
      name: "Sub Admin",
      path: "/subadmin-management",
      icon: "subAdmin-Icon.png",
      moduleKey: "sub-admin-management"
    },
    {
      name: "Static Images",
      path: "/image-management",
      icon: "image-management-icon.png",
      moduleKey: "image-management"
    },
  ];

  // Filter links based on role and permissions
  const filteredLinks = allLinks.filter(link => {
    if (!isSubAdmin) return true; // Show all links for non-SUB_ADMIN users
    
    // For SUB_ADMIN, check if they have view permission for the module
    const hasPermission = viewModules.includes(link.moduleKey);
    
    // Special case for Reservations - show it if user has booking-management permission
    if (link.name === "Reservations") {
      return hasPermission; // Will show if booking-management is in viewModules
    }
    
    return hasPermission;
  });



  const handleLogoClick = () => {
    navigate("/dashboard");
  };

  const confirmLogout = async () => {
    setLoading(true);
    try {
      await dispatch(logout()).unwrap();
      await dispatch(logout_resetState());
      toast.success("You have logged out successfully.");
      navigate("/signin");
    } catch (error) {
      toast.error(`Failed to logout: ${error}`);
    } finally {
      setModalOpen(false);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setModalOpen(true);
  };

  // Handle sidebar toggle with mobile considerations
  const handleToggleSidebar = () => {
    if (isMobileScreen) {
      // On mobile, directly toggle local state
      setLocalIsOpen(!localIsOpen);
      console.log(localIsOpen, "jlocalll");
    } else {
      // On larger screens, use the prop-based toggle
      toggleSidebar();
      setLocalIsOpen(!localIsOpen);
    }
  };
  return (
    <Drawer
      variant={isMobileScreen ? "temporary" : "permanent"}
      open={isOpen}
      onClose={toggleSidebar}
      sx={{
        width: isOpen ? 290 : 80,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          paddingLeft: localIsOpen ? "32px" : 0,
          paddingRight: localIsOpen ? "16px" : 0,
          width: isOpen ? 290 : 80,
          boxSizing: "border-box",
          transition: "width 0.3s ease",
          overflowX: "hidden",
          position: isMobileScreen ? "fixed" : "relative",
          zIndex: theme.zIndex.drawer + 1000,
        },
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <div
        className={`
          flex 
          items-center 
          justify-between 
          p-4 
          transition-all 
          duration-300 
          ease-in-out
        `}
      >
        {localIsOpen && (
          <img
            src="/assets/Aria-Logo.png"
            alt="Aria Logo"
            className={`cursor-pointer 
              h-10 
              transition-all 
              duration-300 
              ease-in-out
              ${localIsOpen ? "opacity-100" : "opacity-0"}
            `}
            onClick={handleLogoClick}
          />
        )}
        <IconButton
          onClick={isMobileScreen ? toggleSidebar : handleToggleSidebar}
        >
          {localIsOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </div>

      {!isLoading && ( // Only render content when not loading


      <div>
        <List sx={{ marginTop: "44px" }}>
        {filteredLinks.map((link, index) => (
            <CustomListItem
              sx={{ justifyContent: "center", minHeight: "48px" }}
              key={index}
              onClick={() => {
                navigate(link.path);
                // Automatically close on mobile after navigation
                if (isMobileScreen) {
                  toggleSidebar();
                }
              }}
              className={`rounded-lg mb-2 ${
                location.pathname === link.path
                  ? "bg-[#a9edf35c] border-[#A9EDF3] border-l-4 font-bold"
                  : "hover:bg-gray-100"
              }`}
            >
              <Tooltip
                title={link.name}
                placement="right"
                slotProps={{
                  popper: {
                    sx: {
                      [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                        {
                          marginTop: "30px",
                        },
                      [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                        {
                          marginBottom: "30px",
                        },
                      [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                        {
                          marginLeft: "30px",
                        },
                      [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                        {
                          marginRight: "30px",
                        },
                    },
                  },
                }}
              >
                <img
                  src={`/assets/${link.icon}`}
                  className={`${localIsOpen ? "mr-3" : "mr-0"}`}
                  alt={`${link.name} icon`}
                />
              </Tooltip>
              {localIsOpen && <ListItemText primary={link.name} />}
            </CustomListItem>
          ))}
        </List>

        <List sx={{ marginTop: { xs: "120px", md: "200px" } }}>
          <CustomListItem
            onClick={handleLogout}
            sx={{
              color: "#FF0000",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "rgba(255, 0, 0, 0.04)",
              },
            }}
          >
            <Tooltip
              title={"Logout"}
              placement="right"
              slotProps={{
                popper: {
                  sx: {
                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                      {
                        marginTop: "30px",
                      },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                      {
                        marginBottom: "30px",
                      },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                      {
                        marginLeft: "50px",
                      },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                      {
                        marginRight: "30px",
                      },
                  },
                },
              }}
            >
              <span className="flex items-center">
                <img
                  src="/assets/logout-Icon.png"
                  className={`${localIsOpen ? "mr-3" : "mr-0"}`}
                  alt="logout icon"
                />
                {localIsOpen && <ListItemText primary="Logout" />}
              </span>
            </Tooltip>
          </CustomListItem>
        </List>
        <CommonConfirmationModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Logging out"
          description="Are you sure you want to Logout?"
          confirmButtonText="Yes"
          onConfirm={confirmLogout}
          loading={loading}
        />
      </div>
            )}

    </Drawer>
  );
};

export default Sidebar;
